import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponseBase } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  Client,
  ClientAdvisors,
  ClientFrontPage,
  ClientHomePage,
  ClientNote,
  ClientSetup,
  PayrollTotals
} from '../models/client';
import { Division } from '../models/division';
import { CompanyLocationConfig } from '../models/company-location-config';
import { CompanyTaxCodeConfig, CompanyLocalTaxConfig } from '../models/tax-config';
import { PagedPayload } from '../models/paged-payload';
import { CompanyDeductionConfig } from '../models/company-deduction-config';
import { CompanyEarningConfig } from '../models/company-earning-config';
import { WorkersCompCode } from '../workers-comp';
import { KeyPair } from '../models/base';
import { ApiResponse } from '../models/api-response';
import { JobCode } from '../models/labor-allocation';
import { Person, UserToPersonMapModel } from '../models/person';
import { PTOConfig } from '../models/paid-time-off';
import { CompanySubTask } from '../models/company-subtask';
import { CompanyContact } from '../models/task';
import { ErrorDetail } from '../models/error-detail';
import { BankInfo, BillingCharge, ManualCheck, ThirdPartyPayee } from '../models/payroll';
import { CompanyLock } from '../models/company-lock';
import { QuarterStatuses } from '../models/quarters';
import { PayStubData, TimeOffList } from '../models/reports';

@Injectable({
  providedIn: 'root'
})
export class CompaniesService {
  constructor(
    private http: HttpClient,
    @Inject('env') private env: any,
    @Inject('errorInterceptorSkipHeader') private errorInterceptorSkipHeader: any
  ) {}

  // #region Client Notes

  public createClientNote(clientId: string, note: ClientNote): Observable<ClientNote> {
    return this.http.post<ClientNote>(`${this._clientUrl(clientId)}/Note`, note);
  }

  public getActiveClientNote(clientId: string): Observable<ClientNote> {
    return this.http.get<ClientNote>(`${this._clientUrl(clientId)}/Note`);
  }

  public getAllClientNotes(clientId: string): Observable<ClientNote[]> {
    return this.http.get<ClientNote[]>(`${this._clientUrl(clientId)}/AllNotes`);
  }

  public updateClientNote(clientId: string, note: ClientNote): Observable<ApiResponse<ClientNote>> {
    const headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');
    return this.http.put<ApiResponse<ClientNote>>(`${this._clientUrl(clientId)}/Note`, note, {
      headers
    });
  }

  public archiveClientNote(clientId: string, note: ClientNote) {
    return this.http.put(`${this._clientUrl(clientId)}/ArchiveNote/`, note);
  }

  // #endregion

  public checkDupeSSN(clientId: string, person: Person): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this._clientUrl(clientId)}/CheckFederalIdentifier`,
      person
    );
  }

  public createJobCode(clientId: string, jobCode: JobCode): Observable<ApiResponse<JobCode>> {
    return this.http.post<ApiResponse<JobCode>>(`${this._clientUrl(clientId)}/JobCodes`, jobCode);
  }

  public getCompanyFrontPage(clientId: string) {
    return this.http.get<ClientFrontPage>(`${this._clientUrl(clientId)}/FrontPage`);
  }

  public getBankInfo(clientId: string, locationId: number): Observable<BankInfo> {
    return this.http.get<BankInfo>(`${this._clientUrl(clientId)}/Location/${locationId}/BankInfo`);
  }

  public getClientBillings(clientId: string): Observable<BillingCharge[]> {
    return this.http.get<BillingCharge[]>(`${this._clientUrl(clientId)}/Billings`);
  }

  public getClientSetup(clientId: string): Observable<ClientSetup> {
    return this.http.get<ClientSetup>(`${this._clientUrl(clientId)}/Setup`, {
      headers: this._getCacheHeader(240)
    });
  }

  public getCompany(clientId: string): Observable<Client> {
    const headers = this._getCacheHeader(5);
    return this.http.get<Client>(this._clientUrl(clientId), { headers });
  }

  public getCompanyContacts(clientId: string): Observable<CompanyContact[]> {
    return this.http.get<CompanyContact[]>(`${this._clientUrl(clientId)}/Contacts`);
  }

  public getCompanyHomepage(clientId: string): Observable<ClientHomePage> {
    return this.http.get<ClientHomePage>(`${this._clientUrl(clientId)}/HomePage`);
  }
  public getClientAdvisors(clientId: string): Observable<ClientAdvisors[]> {
    const headers = this._getCacheHeader(3600);
    return this.http.get<ClientAdvisors[]>(`${this._clientUrl(clientId)}/ClientAdvisors`, {
      headers
    });
  }

  public getDeductions(clientId: string): Observable<CompanyDeductionConfig[]> {
    return this.http.get<CompanyDeductionConfig[]>(`${this._clientUrl(clientId)}/Deductions`);
  }

  public getDivisions(clientId: string): Observable<Division[]> {
    return this.http.get<Division[]>(`${this._clientUrl(clientId)}/Divisions`);
  }

  public getEarnings(clientId: string): Observable<CompanyEarningConfig[]> {
    return this.http.get<CompanyEarningConfig[]>(`${this._clientUrl(clientId)}/Earnings`);
  }

  public getJobCodes(clientId: string): Observable<KeyPair[]> {
    return this.http.get<KeyPair[]>(`${this._clientUrl(clientId)}/JobCodes`);
  }

  public getLastPayrollTotals(clientId: string, locationId: number) {
    return this.http.get<PayrollTotals[]>(
      `${this._clientUrl(clientId)}/Location/0/LastPayrollTotals`
    );
  }

  public getPaylineData(
    clientId: string,
    year: number,
    sequence: number,
    perPage?: number | null,
    page?: number | null
  ) {
    let params = new HttpParams();
    if (perPage != null) {
      params = params.set('per_page', perPage.toString());
    }
    if (page != null) {
      params = params.set('page_number', page.toString());
    }
    return this.http.get<PagedPayload<PayStubData>>(
      `${this._clientUrl(clientId)}/Payroll/${year}/${sequence}/PayStubData`,
      { params }
    );
  }

  public getLocalTaxConfigs(
    clientId: string,
    resident_state: string,
    work_state: string
  ): Observable<CompanyLocalTaxConfig[]> {
    let params = new HttpParams();

    if (resident_state) {
      params = params.set('resident_state', resident_state.trim());
    }

    if (work_state) {
      params = params.set('work_state', work_state.trim());
    }
    const headers = this._getCacheHeader(3600);
    return this.http.get<CompanyLocalTaxConfig[]>(
      `${this._clientUrl(clientId)}/LocalTaxWithholders`,
      { params, headers }
    );
  }

  public getLocalTaxCodeMap(clientId: string, state: string): Observable<CompanyTaxCodeConfig[]> {
    return this.http.get<CompanyTaxCodeConfig[]>(
      `${this._clientUrl(clientId)}/LocalTaxWithholders/CodeMap?state=${state}`
    );
  }

  public getLocations(clientId: string): Observable<CompanyLocationConfig[]> {
    return this.http.get<CompanyLocationConfig[]>(`${this._clientUrl(clientId)}/Locations`, {
      headers: this._getCacheHeader(240)
    });
  }

  // FIXME: this is a payroll call and should be in the payroll service
  public getManualChecks(clientId: string, locationId: string): Observable<ManualCheck[]> {
    return this.http.get<ManualCheck[]>(
      `${this._clientUrl(clientId)}/Location/${locationId}/ManualChecks`
    );
  }

  // FIXME: this is a payroll call and should be in the payroll service
  public getPayrollErrorsAndWarnings(
    clientId: string,
    locationId: string,
    calendarId: string
  ): Observable<ErrorDetail[]> {
    return this.http.get<ErrorDetail[]>(
      `${this._clientUrl(
        clientId
      )}/Location/${locationId}/Calendar/${calendarId}/PayrollErrorsAndWarnings`
    );
  }

  public getPTOConfigs(clientId: string): Observable<PTOConfig[]> {
    return this.http.get<PTOConfig[]>(`${this._clientUrl(clientId)}/PTOConfigs`);
  }

  public getStateTaxExemptions(
    clientId: string,
    stateAbbrev: string
  ): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(
      `${this._clientUrl(clientId)}/StateTaxExemptions?abbrev=${stateAbbrev}`
    );
  }

  public getTaxAgencies(clientId: string) {
    return this.http.get<string[]>(`${this._clientUrl(clientId)}/TaxAgencies`);
  }

  public getTaxConfigs(
    clientId: string,
    includeAllTaxes: boolean = false
  ): Observable<CompanyTaxCodeConfig[]> {
    let params = new HttpParams();
    params = params.append('includeAllTaxes', includeAllTaxes);

    return this.http.get<CompanyTaxCodeConfig[]>(`${this._clientUrl(clientId)}/TaxWithholders`, {
      params
    });
  }

  public getTimeOffCodes(clientId: string) {
    const uri = `${this.env.apiUrlRoot}/Companies/${clientId}/TimeOffCodes`;
    return this.http.get<TimeOffList>(uri);
  }

  public getThirdPartyPayee(clientId: string, id: number): Observable<ThirdPartyPayee> {
    return this.http.get<ThirdPartyPayee>(`${this._clientUrl(clientId)}/ThirdPartyPayees/${id}`);
  }

  public getThirdPartyPayees(clientId: string): Observable<ThirdPartyPayee[]> {
    return this.http.get<ThirdPartyPayee[]>(`${this._clientUrl(clientId)}/ThirdPartyPayees`);
  }

  public getWorkersCompCodes(clientId: string): Observable<WorkersCompCode[]> {
    return this.http.get<WorkersCompCode[]>(`${this._clientUrl(clientId)}/WorkersCompCodes`);
  }

  // FIXME: this is a payroll call and should be in the payroll service
  public updateManualCheck(
    clientId: string,
    payrollYear: number,
    payrollSequenceNumber: number,
    id: number,
    manualCheck: ManualCheck
  ) {
    return this.http.put<HttpResponseBase>(
      `${this._clientUrl(
        clientId
      )}/Payroll/${payrollYear}/${payrollSequenceNumber}/ManualChecks/${id}`,
      manualCheck
    );
  }

  public searchCompanies(args?: {
    name?: string;
    clientId?: string;
    dbaName?: string;
    query?: string;
    pageNumber?: number;
    perPage?: number;
  }) {
    let params = new HttpParams();

    args = args || {
      pageNumber: 1,
      perPage: 50
    };

    args.pageNumber = args.pageNumber || 1;
    args.perPage = args.perPage || 50;

    if (args.name) {
      params = params.set('name', args.name.trim());
    }

    if (args.clientId) {
      params = params.set('client_code', args.clientId.trim());
    }

    if (args.dbaName) {
      params = params.set('dba_name', args.dbaName.trim());
    }

    if (args.query) {
      params = params.set('query', args.query.trim());
    }

    params = params.set('page_number', args.pageNumber.toString());
    params = params.set('per_page', args.perPage.toString());

    return this.http.get<PagedPayload<Client>>(`${this.env.apiUrlRoot}/Companies`, {
      params
    });
  }

  public getQuarterStatus(clientCode: string): Observable<QuarterStatuses> {
    return this.http.get<QuarterStatuses>(`${this._clientUrl(clientCode)}/QuarterStatus`);
  }

  // #region Locks

  public getLock(clientId: string): Observable<ApiResponse<CompanyLock>> {
    return this.http.get<ApiResponse<CompanyLock>>(`${this._clientUrl(clientId)}/Lock`);
  }

  // #endregion

  // #region company subtasks

  public createSubTask(clientId: string, subTask: CompanySubTask) {
    return this.http.post<number>(`${this._clientUrl(clientId)}/SubTasks`, subTask);
  }

  public deleteSubTask(clientId: string, id: number) {
    return this.http.delete<HttpResponseBase>(`${this._clientUrl(clientId)}/SubTasks/${id}`);
  }

  public getSubTask(clientId: string, id: number): Observable<CompanySubTask> {
    return this.http.get<CompanySubTask>(`${this._clientUrl(clientId)}/SubTasks/${id}`);
  }

  public getSubTasks(clientId: string): Observable<CompanySubTask[]> {
    return this.http.get<CompanySubTask[]>(`${this._clientUrl(clientId)}/SubTasks`);
  }

  public updateSubTask(clientId: string, subTask: CompanySubTask) {
    return this.http.put<HttpResponseBase>(
      `${this._clientUrl(clientId)}/SubTasks/${subTask.Id}`,
      subTask
    );
  }

  // #endregion

  // #region Swipeclock

  public updateClientNickName(
    clientId: string,
    userId: string,
    personId: number,
    nickname: string
  ) {
    let model = new UserToPersonMapModel();
    model.ClientCode = clientId;
    model.UserId = userId;
    model.PersonId = personId;
    model.Nickname = nickname;
    return this.http.put<HttpResponseBase>(`${this._clientUrl(clientId)}/Nickname`, model);
  }

  public saveSwipeclockMapping(clientId: string, siteId: number) {
    return this.http.post<number>(
      `${this._clientUrl(clientId)}/SwipeclockMapping?siteId=${siteId}`,
      null
    );
  }

  public getSwipeclockMapping(clientId: string): Observable<number> {
    return this.http.get<number>(`${this._clientUrl(clientId)}/SwipeclockMapping`);
  }

  public saveClientWhitelist(clientId: string) {
    return this.http.post<string>(`${this._clientUrl(clientId)}/Whitelist/Save`, clientId);
  }

  public deleteSwipeclockMapping(clientId: string) {
    return this.http.post<string>(
      `${this._clientUrl(clientId)}/SwipeclockMapping/Delete`,
      clientId
    );
  }

  public deleteClientWhitelist(clientId: string) {
    return this.http.post<string>(`${this._clientUrl(clientId)}/Whitelist/Delete`, clientId);
  }

  public isClientWhitelisted(clientId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this._clientUrl(clientId)}/Whitelisted`);
  }

  public createEmployeePortalEligibility(clientId: string) {
    return this.http.put<string>(
      `${this._clientUrl(clientId)}/CreateEmployeePortalEligibility`,
      clientId
    );
  }

  // #endregion

  // #region PRIVATE  METHODS

  private _clientUrl(clientId: string) {
    return `${this.env.apiUrlRoot}/Companies/${clientId}`;
  }
  private _getCacheHeader(lifetime: number) {
    let headers = new HttpHeaders();
    //We want to cache these requests
    headers = headers.set('X-Cache-Lifetime', `${lifetime}`);
    return headers;
  }

  // #endregion
}
